import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./TestRuleDetails.css";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getApproveORFailRules, getTestRuleDetails } from "../TestRuleRedux";
import { toast, ToastContainer } from "react-toastify";

const DEFAULT_RULE = {
  id: "RUL-1042",
  title: "High-Risk Pregnancy Aspirin Prophylaxis",
  description: "High-Risk Pregnancy Aspirin Prophylaxis",
  createdOn: "Oct 24, 2023",
  createdBy: "Zoe H",
  status: "submitted",
  pathwayEntry: "OB/GYN Intake",
  triggerCodes: ["O09.89", "Z33.1"],
  configuredGuidance: "Consider Aspirin Prophylaxis for High-Risk Pregnancy.",
  recommendedAction: "Review and apply the recommended clinical guidance.",
  testingInstructions:
    "Verify that this rule triggers correctly in the application based on its configured pathway entry point and trigger codes.",
};

const STATUS_LABEL = {
  submitted: "Submitted for Testing",
  passed: "Passed",
  failed: "Failed",
};

function StatusText({ status }) {
  return (
    <span className={`ruleDetail-status ruleDetail-status--${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

/* ===== Approve Rule modal ===== */
function ApproveRuleModal({
  rule,
  busy,
  onCancel,
  onConfirm,
  testRuleDetailsData,
  testRuleDetailsLoading
}) {
  return createPortal(
    <div className="ruleModal-overlay" onMouseDown={onCancel}>
      <div
        className="ruleModal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="approveRuleModalTitle"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ruleModal-header">
          <h2 className="ruleModal-title" id="approveRuleModalTitle">
            Approve Rule?
          </h2>
          <button
            type="button"
            className="ruleModal-close"
            aria-label="Close"
            onClick={onCancel}
          >
            &times;
          </button>
        </div>

        <div className="ruleModal-body">
          <p className="ruleModal-text">
            This rule has passed testing. Please confirm approval to proceed
            with publishing.
          </p>

          <div className="ruleModal-infoTable">
            <div className="ruleModal-infoRow">
              <div className="ruleModal-infoLabel">Rule ID</div>
              <div className="ruleModal-infoValue">
                {testRuleDetailsLoading
                  ? "Loading..."
                  : testRuleDetailsData?.rule_id
                    ? testRuleDetailsData?.rule_id
                    : "-"}
              </div>
            </div>
            <div className="ruleModal-infoRow">
              <div className="ruleModal-infoLabel">Description</div>
              <div className="ruleModal-infoValue">
                {testRuleDetailsLoading
                  ? "Loading..."
                  : testRuleDetailsData?.description
                    ? testRuleDetailsData?.description
                    : "-"}
              </div>
            </div>
          </div>
        </div>

        <div className="ruleModal-footer">
          <button
            type="button"
            className="ruleDetail-btn ruleDetail-btn--ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ruleDetail-btn ruleDetail-btn--primary"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Approving…" : "Approve Rule"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ===== Fail Rule modal ===== */
function FailRuleModal({
  rule,
  busy,
  onCancel,
  onConfirm,
  testRuleDetailsData,
}) {
  const [reason, setReason] = useState("");
  const trimmedReason = reason.trim();

  return createPortal(
    <div className="ruleModal-overlay" onMouseDown={onCancel}>
      <div
        className="ruleModal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="failRuleModalTitle"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ruleModal-header">
          <h2 className="ruleModal-title" id="failRuleModalTitle">
            Fail Rule?
          </h2>
          <button
            type="button"
            className="ruleModal-close"
            aria-label="Close"
            onClick={onCancel}
          >
            &times;
          </button>
        </div>

        <div className="ruleModal-body">
          <p className="ruleModal-text">
            This rule did not pass testing. Please provide a reason for the
            failure.
          </p>

          <div className="ruleModal-infoTable ruleModal-infoTable--inline">
            <div className="ruleModal-infoRow ruleModal-infoRow--inline">
              <div className="ruleModal-infoLabel">Rule ID</div>
              <div className="ruleModal-infoValue">
                {testRuleDetailsData?.rule_id || "-"}
              </div>
            </div>
            <div className="ruleModal-infoRow ruleModal-infoRow--inline">
              <div className="ruleModal-infoLabel">Description</div>
              <div className="ruleModal-infoValue">
                {testRuleDetailsData?.description}
              </div>
            </div>
          </div>

          <div className="ruleModal-fieldLabel">
            Failure Reason <span className="ruleModal-required">*</span>
          </div>
          <textarea
            className="ruleModal-textarea"
            placeholder="Describe why the rule failed testing."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
          />
        </div>

        <div className="ruleModal-footer">
          <button
            type="button"
            className="ruleDetail-btn ruleDetail-btn--ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ruleDetail-btn ruleDetail-btn--danger ruleDetail-btn--dangerSolid"
            onClick={() => onConfirm(trimmedReason)}
            disabled={busy || !trimmedReason}
          >
            {busy ? "Failing…" : "Confirm Fail"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function TestRuleDetails({
  rule = DEFAULT_RULE,
  onApprove,
}) {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(null); // "fail" | "approve" | null
  const [activeModal, setActiveModal] = useState(null); // "approve" | "fail" | null
  const navigate = useNavigate();
  const {
    loading: testRuleDetailsLoading,
    data: testRuleDetailsData,
    error: testRuleDetailsError,
  } = useSelector((state) => state.testRule.testRuleDetails);

  const handleFail = async (reason) => {
    setBusy("fail");

    try {
      const payload = {
        code_assist_manual_rule_id:
          testRuleDetailsData?.code_assist_manual_rule_id,
        action: "FAIL",
        reason: reason || "",
      };

      // const result = await dispatch(getApproveORFailRules(payload));

      // console.log("Rule marked as failed:", result);

      // Close modal and navigate only after successful API call
      setActiveModal(null);
      const queryParams = new URLSearchParams({
        code_assist_manual_rule_id:
          testRuleDetailsData?.code_assist_manual_rule_id,
        action: "FAIL",
        reason: reason || "",
      });

      navigate(`/failed-rule-details?${queryParams.toString()}`);
    } catch (err) {
      console.error("Failed to mark rule as failed:", err);
    } finally {
      setBusy(null);
      setActiveModal(null);
    }
  };

  const handleApprove = async () => {
    setBusy("approve");
    try {
      const payload = {
        code_assist_manual_rule_id:
          testRuleDetailsData?.code_assist_manual_rule_id,
        action: "APPROVE",
        reason: "",
      };
      // const result = await dispatch(getApproveORFailRules(payload));
      // console.log("Rule marked as approved:", result);
      setActiveModal(null);
      const queryParams = new URLSearchParams({
        code_assist_manual_rule_id:
          testRuleDetailsData?.code_assist_manual_rule_id,
        action: "APPROVE",
        reason: "",
      });
      sessionStorage.removeItem("environment-available");
      navigate(`/approved-rule-details?${queryParams.toString()}`);
    } catch (err) {
      console.error("Failed to approve rule:", err);
      // error will be shown on the next page instead
    } finally {
      setBusy(null);
      setActiveModal(null);
    }
  };

useEffect(() => {
  const fetchTestRuleDetails = async () => {
    const testRuleID = sessionStorage.getItem("test-rule-id");
    if (!testRuleID) return;
    try {
      const response = await dispatch(
        getTestRuleDetails({
          code_assist_manual_rule_id: testRuleID,
        }),
      ).unwrap();
    } catch (error) {
      toast.error("Edit rule failed");
      console.error(error, "edit testRule error");
    }
  };
  fetchTestRuleDetails();
}, [dispatch]);

  return (
    <div className="ruleDetail-page">
      <ToastContainer position="top-right" autoClose={3000} />    
      {/* Header */}
      <div className="ruleDetail-header">
        <div className="ruleDetail-header-title-row">
          <h1 className="ruleDetail-title">
            {testRuleDetailsLoading
              ? "Description..."
              : testRuleDetailsData?.description
                ? testRuleDetailsData?.description
                : "No Title"}
          </h1>
          <span className="ruleDetail-id-pill">
            {testRuleDetailsLoading
              ? "Rule ID..."
              : testRuleDetailsData?.rule_id
                ? testRuleDetailsData?.rule_id
                : "No Rule ID"}
          </span>
          <span className="ruleDetail-header-divider" aria-hidden="true" />
          <StatusText status={testRuleDetailsData?.status || "No status"} />
        </div>
      </div>

      {/* Rule Information */}
      <section className="ruleDetail-card">
        <div className="ruleDetail-card-header">Rule Information</div>
        <div className="ruleDetail-card-body">
          <div className="ruleDetail-grid">
            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Rule ID</div>
              <div className="ruleDetail-value">
                {testRuleDetailsLoading
                  ? "Loading..."
                  : testRuleDetailsData?.rule_id
                    ? testRuleDetailsData?.rule_id
                    : "-"}
              </div>
            </div>
            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Rule Description</div>
              <div className="ruleDetail-value">
                {testRuleDetailsLoading
                  ? "Loading..."
                  : testRuleDetailsData?.description
                    ? testRuleDetailsData?.description
                    : "-"}
              </div>
            </div>

            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Created On</div>
              <div className="ruleDetail-value">
                {testRuleDetailsLoading
                  ? "Loading..."
                  : testRuleDetailsData?.created_on
                    ? testRuleDetailsData?.created_on
                    : "-"}
              </div>
            </div>
            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Created By</div>
              <div className="ruleDetail-value">
                {testRuleDetailsLoading
                  ? "Loading..."
                  : testRuleDetailsData?.created_by
                    ? testRuleDetailsData?.created_by
                    : "-"}
              </div>
            </div>

            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Status</div>
              <div className="ruleDetail-value">
                {testRuleDetailsLoading
                  ? "Loading..."
                  : STATUS_LABEL[testRuleDetailsData?.status] ||
                    testRuleDetailsData?.status ||
                    "-"}
              </div>
            </div>
            <div className="ruleDetail-field">
              <div className="ruleDetail-label">Pathway Entry Point</div>
              <div className="ruleDetail-value">
                {testRuleDetailsLoading ? "Loading..." : 
                <>
                <span>{testRuleDetailsData?.pathway_entry?.type}</span> /{" "}
                <span>{testRuleDetailsData?.pathway_entry?.value}</span>
                </>
              
                }
              </div>
            </div>

            <div className="ruleDetail-field ruleDetail-field--full">
              <div className="ruleDetail-label">Trigger Codes</div>
              <div className="ruleDetail-tags">
                {testRuleDetailsLoading ? (
  "Loading..."
) : (testRuleDetailsData?.trigger_codes || []).length > 0 ? (
  testRuleDetailsData.trigger_codes.map((item, index) => (
    <span
      className="ruleDetail-tag"
      key={`${item?.code}-${index}`}
    >
      {item?.code || "No code"}
    </span>
  ))
) : (
  <span className="ruleDetail-tag">No code</span>
)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guidance & Decision Support */}
      <section className="ruleDetail-card">
        <div className="ruleDetail-card-header">
          Guidance &amp; Decision Support
        </div>
        <div className="ruleDetail-card-body">
          <div className="ruleDetail-label">Configured Guidance</div>
          <div className="ruleDetail-guidance-box">
            &ldquo;
            {testRuleDetailsLoading
                  ? "Loading..."
                  : testRuleDetailsData?.guidance?.configured_guidance
                  ? testRuleDetailsData?.guidance?.configured_guidance
                  : "-"}
                  &rdquo;
          </div>

          <div className="ruleDetail-label ruleDetail-label--spaced">
            Recommended Action
          </div>
          <div className="ruleDetail-value">
            {testRuleDetailsLoading
                  ? "Loading..."
                  : testRuleDetailsData?.guidance?.recommended_action
                    ? testRuleDetailsData?.guidance?.recommended_action
                    : "-"}
          </div>
        </div>
      </section>

      {/* Testing */}
      <section className="ruleDetail-card">
        <div className="ruleDetail-card-header">Testing</div>
        <div className="ruleDetail-card-body">
          <p className="ruleDetail-testing-text">
            {rule.testingInstructions}</p>
        </div>
      </section>

      {/* Footer actions */}
      <div className="ruleDetail-footer">
        <button
          type="button"
          className="ruleDetail-btn ruleDetail-btn--ghost"
          onClick={() => {
            navigate(`/test-rule-table`);
          }}
        >
          Back
        </button>
        <div className="ruleDetail-footer-actions">
          <button
            type="button"
            className="ruleDetail-btn ruleDetail-btn--danger"
            onClick={() => setActiveModal("fail")}
            disabled={busy !== null}
          >
            Fail Rule
          </button>
          <button
            type="button"
            className="ruleDetail-btn ruleDetail-btn--primary"
            onClick={() => setActiveModal("approve")}
            disabled={busy !== null}
          >
            Approve Rule
          </button>
        </div>
      </div>

      {/* Modals */}
      {activeModal === "approve" && (
        <ApproveRuleModal
          rule={rule}
          testRuleDetailsData={testRuleDetailsData}
          testRuleDetailsLoading={testRuleDetailsLoading}
          busy={busy === "approve"}
          onCancel={() => setActiveModal(null)}
          onConfirm={handleApprove}
        />
      )}
      {activeModal === "fail" && (
        <FailRuleModal
          rule={rule}
          testRuleDetailsData={testRuleDetailsData}
          testRuleDetailsLoading={testRuleDetailsLoading}
          busy={busy === "fail"}
          onCancel={() => setActiveModal(null)}
          onConfirm={handleFail}
        />
      )}
    </div>
  );
}
